
import React, { useState, useEffect, useMemo } from 'react';
import { FileSpreadsheet, Plus, Edit2, Trash2, CheckCircle2, Circle, X, Save, Search, BarChart2, Calendar, ClipboardCheck, Loader2, Activity, Download, CheckSquare, Square, FileText, ChevronDown, ChevronUp, Layers } from 'lucide-react';
import { dbService } from '../lib/firebase';
import { OnCallCaseLog, SurgicalLog } from '../types';
import { USERS, PROCEDURES_LIST } from '../constants';

/**
 * Robust normalization to ensure "Grisham", "GRISHAM", and "Grisham " 
 * all aggregate to a single entry. Collapses multiple internal spaces.
 */
const toTitleCase = (str: string | undefined | null) => {
    if (!str) return '';
    const trimmed = str.trim();
    if (!trimmed) return '';
    // Split by any whitespace (including multiple spaces) to normalize internal gaps
    return trimmed.toLowerCase().split(/\s+/).map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
};

/**
 * Check if a string is a placeholder for "no entry" (e.g., "None", "N/A")
 */
const isPlaceholder = (str: string) => {
    const val = str.toLowerCase().trim();
    return !val || val === 'none' || val === 'na' || val === 'n/a' || val === 'no' || val === '-';
};

export const CaseLogs: React.FC = () => {
  // LANDING PAGE: Default to Resident Logbook as requested
  const [activeTab, setActiveTab] = useState<'tracker' | 'master' | 'resident_log'>('resident_log');
  const [masterSheetUrl, setMasterSheetUrl] = useState<string>('');
  const [logs, setLogs] = useState<OnCallCaseLog[]>([]);
  const [surgicalLogs, setSurgicalLogs] = useState<SurgicalLog[]>([]);
  const [loading, setLoading] = useState(true);

  // Tracker State - Default to current year dynamically to ensure today's cases show up
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
  const [trackerSubView, setTrackerSubView] = useState<'log' | 'summary'>('log');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLogId, setEditingLogId] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<OnCallCaseLog>>({});

  // Delete Confirmation State (On Call)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Resident Logbook State
  const [logView, setLogView] = useState<'log' | 'summary'>('log');
  const [activeHospitalTab, setActiveHospitalTab] = useState<'MVES' | 'OPVES'>('MVES');
  const [surgicalSearch, setSurgicalSearch] = useState('');
  const [editingSurgicalLogId, setEditingSurgicalLogId] = useState<string | null>(null);
  
  // Collapsible Doctor Stats State
  const [expandedDoctors, setExpandedDoctors] = useState<Record<string, boolean>>({});

  // Month-by-month grouping states
  const [expandedTrackerMonths, setExpandedTrackerMonths] = useState<Record<string, boolean>>({});
  const [expandedSurgicalMonths, setExpandedSurgicalMonths] = useState<Record<string, boolean>>({});

  const toggleTrackerMonth = (month: string) => {
      setExpandedTrackerMonths(prev => ({...prev, [month]: !prev[month]}));
  };
  
  const toggleSurgicalMonth = (month: string) => {
      setExpandedSurgicalMonths(prev => ({...prev, [month]: !prev[month]}));
  };

  const getMonthName = (dateStr: string) => {
      const parts = dateStr.split('-');
      if (parts.length < 2) return 'Unknown';
      const monthNum = parseInt(parts[1], 10);
      const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
      return monthNames[monthNum - 1] || 'Unknown';
  };

  const [surgicalForm, setSurgicalForm] = useState<Partial<SurgicalLog>>({
      date: new Date().toISOString().split('T')[0],
      hospital: 'MVES',
      patientName: '',
      caseNumber: '',
      attending: '',
      residentOne: '',
      residentTwo: '',
      procedure: '',
      residentOneCertLogged: false,
      residentTwoCertLogged: false
  });
  // Toggle for adding to On-Call Tracker
  const [addToOnCall, setAddToOnCall] = useState(false);
  const [onCallExtras, setOnCallExtras] = useState({
      anesthesiaSupport: '',
      referringErDoctor: ''
  });

  // Delete Confirmation State (Surgical Log)
  const [surgicalCaseToDelete, setSurgicalCaseToDelete] = useState<SurgicalLog | null>(null);

  // Lists for dropdowns
  const attendings = useMemo(() => USERS.filter(u => u.role === 'Attending').map(u => u.lastName).sort(), []);
  const residents = useMemo(() => USERS.filter(u => u.role === 'Resident').map(u => u.lastName).sort(), []);

  // Initial Data & Real-time Subs
  useEffect(() => {
    dbService.getAppConfig().then(config => setMasterSheetUrl(config.masterSheetUrl));

    const unsubOnCall = dbService.subscribeToOnCallLogs((data) => {
        setLogs(data);
        setLoading(false);
    });
    const unsubSurgical = dbService.subscribeToSurgicalLogs((data) => {
        setSurgicalLogs(data);
    });

    return () => {
        unsubOnCall();
        unsubSurgical();
    };
  }, []);

  const handleAddNew = () => {
      setEditingLogId(null);
      setForm({
          date: new Date().toISOString().split('T')[0],
          location: 'MVES',
          patientFirstName: '',
          patientLastName: '',
          caseNumber: '',
          referringErDoctor: '',
          attending: '',
          resident: '',
          anesthesiaSupport: '',
          procedure: '',
          payrollCompleted: false
      });
      setIsModalOpen(true);
  };

  const handleEdit = (e: React.MouseEvent, log: OnCallCaseLog) => {
      e.stopPropagation();
      e.preventDefault();
      setDeleteConfirmId(null);
      setEditingLogId(log.id);
      setForm({ ...log });
      setIsModalOpen(true);
  };

  const initiateDelete = (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      e.preventDefault();
      setDeleteConfirmId(id);
  };

  const cancelDelete = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setDeleteConfirmId(null);
  };

  const confirmDelete = async (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      e.preventDefault();
      setDeleteConfirmId(null);
      await dbService.deleteOnCallLog(id);
  };

  const handleSave = async () => {
      if (!form.date || !form.caseNumber || !form.patientLastName) {
          alert("Please fill in required fields (Date, Case #, Last Name).");
          return;
      }

      const newLog: OnCallCaseLog = {
          id: editingLogId || `log-${Date.now()}`,
          date: form.date!,
          location: form.location as 'MVES' | 'OPVES',
          patientFirstName: form.patientFirstName || '',
          patientLastName: form.patientLastName || '',
          caseNumber: form.caseNumber || '',
          referringErDoctor: form.referringErDoctor || '',
          attending: toTitleCase(form.attending),
          resident: toTitleCase(form.resident),
          anesthesiaSupport: toTitleCase(form.anesthesiaSupport),
          procedure: form.procedure || '',
          payrollCompleted: form.payrollCompleted || false
      };

      setIsModalOpen(false);
      await dbService.saveOnCallLog(newLog);
  };

  const togglePayroll = async (e: React.MouseEvent, log: OnCallCaseLog) => {
      e.stopPropagation();
      e.preventDefault();
      const updatedLog = { ...log, payrollCompleted: !log.payrollCompleted };
      await dbService.saveOnCallLog(updatedLog);
  };

  // --- Filtering Logic (On-Call Tracker) ---
  const filteredLogs = useMemo(() => {
      return logs.filter(log => {
          const yearMatch = log.date.startsWith(selectedYear);
          const term = searchTerm.toLowerCase();
          const searchMatch = 
            log.patientLastName.toLowerCase().includes(term) ||
            log.caseNumber.toLowerCase().includes(term) ||
            log.procedure.toLowerCase().includes(term) ||
            log.attending.toLowerCase().includes(term) ||
            log.resident.toLowerCase().includes(term);
          return yearMatch && searchMatch;
      }).sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [logs, searchTerm, selectedYear]);

  const groupedTrackerLogs = useMemo(() => {
      const groups: { month: string, logs: OnCallCaseLog[] }[] = [];
      const monthMap = new Map<string, OnCallCaseLog[]>();
      
      filteredLogs.forEach(log => {
          const month = getMonthName(log.date);
          if (!monthMap.has(month)) {
              monthMap.set(month, []);
          }
          monthMap.get(month)!.push(log);
      });
      
      monthMap.forEach((logs, month) => {
          groups.push({ month, logs });
      });
      return groups;
  }, [filteredLogs]);

  // --- Stats Calculation (On-Call Tracker) ---
  const stats = useMemo(() => {
      const breakdown = {
          attending: {} as Record<string, number>,
          resident: {} as Record<string, number>,
          location: { MVES: 0, OPVES: 0 },
          anesthesia: {} as Record<string, number>
      };

      filteredLogs.forEach(log => {
          const att = toTitleCase(log.attending) || 'Unknown';
          if (!isPlaceholder(att)) {
              breakdown.attending[att] = (breakdown.attending[att] || 0) + 1;
          }
          
          const res = toTitleCase(log.resident) || 'Unknown';
          if (!isPlaceholder(res)) {
              breakdown.resident[res] = (breakdown.resident[res] || 0) + 1;
          }

          if (log.location === 'MVES') breakdown.location.MVES++;
          if (log.location === 'OPVES') breakdown.location.OPVES++;

          const anes = toTitleCase(log.anesthesiaSupport);
          if (!isPlaceholder(anes)) {
              breakdown.anesthesia[anes] = (breakdown.anesthesia[anes] || 0) + 1;
          }
      });

      return breakdown;
  }, [filteredLogs]);

  // --- SURGICAL RESIDENT LOG FUNCTIONS ---

  const handleSurgicalSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!surgicalForm.procedure) {
          alert("Please select a procedure.");
          return;
      }
      
      const newCase: SurgicalLog = {
          id: editingSurgicalLogId || `sl-${Date.now()}`,
          date: surgicalForm.date!,
          hospital: surgicalForm.hospital as 'MVES' | 'OPVES',
          patientName: surgicalForm.patientName || '',
          caseNumber: surgicalForm.caseNumber || '',
          attending: surgicalForm.attending || '',
          residentOne: surgicalForm.residentOne || '',
          residentTwo: surgicalForm.residentTwo || '',
          procedure: surgicalForm.procedure || '',
          residentOneCertLogged: surgicalForm.residentOneCertLogged || false,
          residentTwoCertLogged: surgicalForm.residentTwoCertLogged || false,
          timestamp: new Date().toISOString()
      };

      await dbService.saveSurgicalLog(newCase);

      if (addToOnCall) {
          const nameParts = (surgicalForm.patientName || '').trim().split(/\s+/);
          let lastName = '';
          let firstName = '';
          
          if (nameParts.length > 1) {
              lastName = nameParts.pop() || '';
              firstName = nameParts.join(' ');
          } else {
              firstName = nameParts[0] || '';
              lastName = ''; 
          }

          const newOnCallLog: OnCallCaseLog = {
              id: `log-${Date.now()}-oc`, 
              date: surgicalForm.date!,
              location: surgicalForm.hospital as 'MVES' | 'OPVES',
              patientFirstName: firstName,
              patientLastName: lastName,
              caseNumber: surgicalForm.caseNumber || '',
              referringErDoctor: onCallExtras.referringErDoctor,
              attending: toTitleCase(surgicalForm.attending),
              resident: toTitleCase(surgicalForm.residentOne),
              anesthesiaSupport: toTitleCase(onCallExtras.anesthesiaSupport),
              procedure: surgicalForm.procedure || '',
              payrollCompleted: false
          };

          await dbService.saveOnCallLog(newOnCallLog);
      }

      handleCancelSurgicalEdit();
  };

  const handleEditSurgicalLog = (log: SurgicalLog) => {
      setEditingSurgicalLogId(log.id);
      setSurgicalForm({
          date: log.date,
          hospital: log.hospital,
          patientName: log.patientName,
          caseNumber: log.caseNumber,
          attending: log.attending,
          residentOne: log.residentOne,
          residentTwo: log.residentTwo,
          procedure: log.procedure,
          residentOneCertLogged: log.residentOneCertLogged,
          residentTwoCertLogged: log.residentTwoCertLogged
      });
      setLogView('log');
      window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelSurgicalEdit = () => {
      setEditingSurgicalLogId(null);
      setSurgicalForm({
          date: new Date().toISOString().split('T')[0],
          hospital: 'MVES',
          patientName: '',
          caseNumber: '',
          attending: '',
          residentOne: '',
          residentTwo: '',
          procedure: '',
          residentOneCertLogged: false,
          residentTwoCertLogged: false
      });
      setAddToOnCall(false);
      setOnCallExtras({ anesthesiaSupport: '', referringErDoctor: '' });
  };

  const toggleSurgicalCert = async (caseId: string, residentField: 'residentOne' | 'residentTwo') => {
      const log = surgicalLogs.find(l => l.id === caseId);
      if (!log) return;

      const updatedLog = { ...log };
      if (residentField === 'residentOne') {
          updatedLog.residentOneCertLogged = !log.residentOneCertLogged;
      } else {
          updatedLog.residentTwoCertLogged = !log.residentTwoCertLogged;
      }

      await dbService.saveSurgicalLog(updatedLog);
  };

  const initiateSurgicalDelete = (log: SurgicalLog) => {
      setSurgicalCaseToDelete(log);
  };

  const confirmSurgicalDelete = async () => {
      if (!surgicalCaseToDelete) return;
      await dbService.deleteSurgicalLog(surgicalCaseToDelete.id);
      setSurgicalCaseToDelete(null);
  };

  const toggleDoctorStats = (doc: string) => {
      setExpandedDoctors(prev => ({...prev, [doc]: !prev[doc]}));
  };

  const searchedSurgicalLogs = useMemo(() => {
      if (!surgicalSearch) return surgicalLogs;
      const term = surgicalSearch.toLowerCase();
      return surgicalLogs.filter(log => 
          (log.attending || '').toLowerCase().includes(term) ||
          (log.residentOne || '').toLowerCase().includes(term) ||
          (log.residentTwo || '').toLowerCase().includes(term) ||
          (log.patientName || '').toLowerCase().includes(term) ||
          (log.caseNumber || '').toLowerCase().includes(term) ||
          (log.procedure || '').toLowerCase().includes(term)
      );
  }, [surgicalLogs, surgicalSearch]);

  const visibleSurgicalLogs = searchedSurgicalLogs.filter(c => c.hospital === activeHospitalTab);

  const groupedSurgicalLogs = useMemo(() => {
      const groups: { month: string, logs: SurgicalLog[] }[] = [];
      const monthMap = new Map<string, SurgicalLog[]>();
      
      const sorted = [...visibleSurgicalLogs].sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      
      sorted.forEach(log => {
          const month = getMonthName(log.date);
          if (!monthMap.has(month)) {
              monthMap.set(month, []);
          }
          monthMap.get(month)!.push(log);
      });
      
      monthMap.forEach((logs, month) => {
          groups.push({ month, logs });
      });
      return groups;
  }, [visibleSurgicalLogs]);

  const handleSurgicalExport = () => {
      const escapeCSV = (str: any) => {
          if (str === null || str === undefined) return '';
          const stringValue = String(str);
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
      };

      const headers = ['Date', 'Hospital', 'Patient Name', 'Case Number', 'Attending', 'Resident 1', 'Res 1 CERT', 'Resident 2', 'Res 2 CERT', 'Procedure'];
      
      const logsToExport = searchedSurgicalLogs;

      const csvContent = [
          headers.join(','),
          ...logsToExport.map(c => [
              escapeCSV(c.date),
              escapeCSV(c.hospital),
              escapeCSV(c.patientName),
              escapeCSV(c.caseNumber),
              escapeCSV(c.attending),
              escapeCSV(c.residentOne),
              escapeCSV(c.residentOneCertLogged ? "Yes" : "No"),
              escapeCSV(c.residentTwo),
              escapeCSV(c.residentTwoCertLogged ? "Yes" : "No"),
              escapeCSV(c.procedure)
          ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `surgical_cases_export_${new Date().toISOString().slice(0,10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  };

  const getSurgicalCategory = (procedureString: string) => {
      if (!procedureString) return "Unknown";
      const parts = procedureString.split(' - ');
      return parts.slice(0, 3).join(' - ');
  };

  const surgicalGlobalStats = useMemo(() => {
      const stats: Record<string, number> = {};
      surgicalLogs.forEach(c => {
          const category = getSurgicalCategory(c.procedure);
          stats[category] = (stats[category] || 0) + 1;
      });
      return stats;
  }, [surgicalLogs]);

  const getDoctorStats = (doctorName: string) => {
      const normalizedSearchName = doctorName.trim().toUpperCase();
      const doctorCases = surgicalLogs.filter(c => 
          (c.attending || '').trim().toUpperCase() === normalizedSearchName || 
          (c.residentOne || '').trim().toUpperCase() === normalizedSearchName || 
          (c.residentTwo || '').trim().toUpperCase() === normalizedSearchName
      );
      const stats: Record<string, number> = {};
      doctorCases.forEach(c => {
          const category = getSurgicalCategory(c.procedure);
          stats[category] = (stats[category] || 0) + 1;
      });
      return { stats, total: doctorCases.length };
  };

  const StatBox = ({ title, data, total }: { title: string, data: Record<string, number>, total: number }) => (
    <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col min-w-[200px]">
        <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
            <Activity className="w-3 h-3" /> {title}
        </h4>
        <div className="space-y-2 flex-1">
            {Object.entries(data).length === 0 ? (
                <div className="text-xs text-slate-400 italic">No data.</div>
            ) : (
                Object.entries(data)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 4)
                    .map(([key, count]) => (
                        <div key={key} className="flex flex-col gap-1">
                            <div className="flex justify-between items-center text-xs">
                                <span className="font-bold text-slate-700 dark:text-slate-300 truncate pr-2">{key}</span>
                                <span className="text-slate-500 font-mono">{count}</span>
                            </div>
                            <div className="w-full h-1 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                <div className="h-full bg-blue-500 dark:bg-blue-600 rounded-full" style={{ width: `${(count / (total || 1)) * 100}%` }}></div>
                            </div>
                        </div>
                    ))
            )}
        </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-slate-100 dark:bg-slate-950">
      {/* Navigation Bar */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 pt-4 flex items-end gap-2 shadow-sm z-10 overflow-x-auto">
        <button
          onClick={() => setActiveTab('resident_log')}
          className={`flex items-center gap-2 px-6 py-3 font-bold text-sm transition-all border-b-4 rounded-t-lg whitespace-nowrap ${
            activeTab === 'resident_log'
              ? 'border-blue-600 text-blue-800 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
          }`}
        >
          <FileText className="w-4 h-4" /> Resident Logbook
        </button>
        <button
          onClick={() => setActiveTab('tracker')}
          className={`flex items-center gap-2 px-6 py-3 font-bold text-sm transition-all border-b-4 rounded-t-lg whitespace-nowrap ${
            activeTab === 'tracker'
              ? 'border-indigo-600 text-indigo-800 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
          }`}
        >
          <ClipboardCheck className="w-4 h-4" /> On-Call Tracker
        </button>
        <button
          onClick={() => setActiveTab('master')}
          className={`flex items-center gap-2 px-6 py-3 font-bold text-sm transition-all border-b-4 rounded-t-lg whitespace-nowrap ${
            activeTab === 'master'
              ? 'border-slate-500 text-slate-800 dark:text-slate-200 bg-slate-100 dark:bg-slate-800'
              : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
          }`}
        >
          <FileSpreadsheet className="w-4 h-4" /> External Master Sheet
        </button>
      </div>

      <div className="flex-1 overflow-hidden relative bg-slate-50 dark:bg-[#0f172a] flex flex-col">
        {activeTab === 'tracker' && (
            <div className="flex-1 flex flex-col overflow-hidden">
                <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex flex-col md:flex-row justify-between items-center gap-4 bg-white dark:bg-slate-800 shadow-sm z-20">
                    <div className="flex gap-4 w-full md:w-auto">
                        <div className="relative flex-1 md:w-64">
                            <input
                                type="text"
                                placeholder="Search by name, case #, doctor..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                            />
                            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                        </div>
                        <div className="relative w-32">
                             <select 
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm appearance-none bg-white dark:bg-slate-900 font-bold text-slate-700 dark:text-slate-300"
                             >
                                 <option value="2025">2025</option>
                                 <option value="2026">2026</option>
                             </select>
                             <Calendar className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
                        </div>
                    </div>

                    <div className="flex bg-slate-100 dark:bg-slate-900 p-1 rounded-lg">
                        <button
                            onClick={() => setTrackerSubView('log')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-bold transition-all ${
                                trackerSubView === 'log' ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-400 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                            }`}
                        >
                            <ClipboardCheck className="w-4 h-4" />
                            Case Log
                        </button>
                        <button
                            onClick={() => setTrackerSubView('summary')}
                            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-bold transition-all ${
                                trackerSubView === 'summary' ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-400 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                            }`}
                        >
                            <BarChart2 className="w-4 h-4" />
                            Summary
                        </button>
                    </div>

                    <div className="flex gap-3 items-center w-full md:w-auto justify-between md:justify-end">
                        <div className="text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 whitespace-nowrap">
                            <span className="font-bold text-slate-800 dark:text-slate-200">{filteredLogs.length}</span> cases
                        </div>
                        <button 
                            onClick={handleAddNew}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-colors shadow-sm text-sm whitespace-nowrap"
                        >
                            <Plus className="w-4 h-4" /> Add Case
                        </button>
                    </div>
                </div>

                <div className="flex-1 flex flex-col overflow-y-auto">
                    {trackerSubView === 'summary' ? (
                        <div className="p-6 bg-slate-50 dark:bg-slate-900/30 min-h-full animate-in fade-in duration-300">
                             <div className="max-w-7xl mx-auto">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                                            <Layers className="w-4 h-4" />
                                        </div>
                                        <h3 className="font-bold text-slate-800 dark:text-white brand-font">On-Call Analytics Dashboard</h3>
                                    </div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-white dark:bg-slate-800 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 shadow-sm">
                                        Year: {selectedYear}
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <StatBox title="Top Attendings" data={stats.attending} total={filteredLogs.length} />
                                    <StatBox title="Top Residents" data={stats.resident} total={filteredLogs.length} />
                                    <StatBox title="Hospital Mix" data={stats.location} total={filteredLogs.length} />
                                    <StatBox title="Anesthesia Support" data={stats.anesthesia} total={filteredLogs.length} />
                                </div>
                             </div>
                        </div>
                    ) : (
                        <div className="flex-1 animate-in fade-in duration-300">
                            <table className="w-full text-left text-sm whitespace-nowrap">
                                <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400 font-bold border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10 shadow-sm">
                                    <tr>
                                        <th className="p-4">Date</th>
                                        <th className="p-4">Payroll</th>
                                        <th className="p-4">Loc</th>
                                        <th className="p-4">Patient</th>
                                        <th className="p-4">Case #</th>
                                        <th className="p-4">Attending</th>
                                        <th className="p-4">Resident</th>
                                        <th className="p-4">Anesthesia</th>
                                        <th className="p-4">Referring ER</th>
                                        <th className="p-4 w-full">Procedure</th>
                                        <th className="p-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                {loading ? (
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                        <tr><td colSpan={11} className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></td></tr>
                                    </tbody>
                                ) : groupedTrackerLogs.length === 0 ? (
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                        <tr><td colSpan={11} className="p-10 text-center text-slate-400 italic">No logs found for this period.</td></tr>
                                    </tbody>
                                ) : (
                                    groupedTrackerLogs.map(({ month, logs }, index) => {
                                        const isExpanded = expandedTrackerMonths[month] !== undefined ? expandedTrackerMonths[month] : index === 0;
                                        return (
                                        <React.Fragment key={month}>
                                            <tbody>
                                                <tr 
                                                    className="bg-slate-100 dark:bg-slate-800 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors" 
                                                    onClick={() => toggleTrackerMonth(month)}
                                                >
                                                    <td colSpan={11} className="p-3 font-bold text-slate-800 dark:text-slate-200 border-b border-slate-200 dark:border-slate-700">
                                                        <div className="flex items-center gap-2">
                                                            {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                                                            {month} <span className="text-xs font-normal text-slate-500 bg-white dark:bg-slate-900 px-2 py-0.5 rounded-full ml-2 border border-slate-200 dark:border-slate-700">{logs.length} cases</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            </tbody>
                                            {isExpanded && (
                                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                                    {logs.map(log => (
                                                        <tr key={log.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors group">
                                                <td className="p-4 font-mono text-slate-600 dark:text-slate-400">{log.date}</td>
                                                <td className="p-4">
                                                    <button 
                                                        type="button"
                                                        onClick={(e) => togglePayroll(e, log)} 
                                                        className="transition-transform active:scale-95 focus:outline-none relative z-10"
                                                        title="Toggle Payroll Status"
                                                    >
                                                        {log.payrollCompleted ? (
                                                            <div className="flex items-center gap-1 text-green-600 dark:text-green-400 font-bold text-xs bg-green-50 dark:bg-green-900/30 px-2 py-0.5 rounded-full border border-green-200 dark:border-green-800">
                                                                <CheckCircle2 className="w-3 h-3" /> Paid
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-1 text-slate-400 font-medium text-xs bg-slate-100 dark:bg-slate-900 px-2 py-0.5 rounded-full border border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-300">
                                                                <Circle className="w-3 h-3" /> Pending
                                                            </div>
                                                        )}
                                                    </button>
                                                </td>
                                                <td className="p-4">
                                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${log.location === 'MVES' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800' : 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800'}`}>
                                                        {log.location}
                                                    </span>
                                                </td>
                                                <td className="p-4 font-bold text-slate-800 dark:text-slate-200">{log.patientLastName}, {log.patientFirstName}</td>
                                                <td className="p-4 font-mono text-slate-600 dark:text-slate-400">{log.caseNumber}</td>
                                                <td className="p-4 text-indigo-700 dark:text-indigo-400 font-bold text-xs uppercase">{log.attending}</td>
                                                <td className="p-4 text-blue-600 dark:text-blue-400 font-bold text-xs uppercase">{log.resident}</td>
                                                <td className="p-4 text-slate-600 dark:text-slate-400">{log.anesthesiaSupport}</td>
                                                <td className="p-4 text-slate-600 dark:text-slate-400 text-xs truncate max-w-[150px]">{log.referringErDoctor}</td>
                                                <td className="p-4 font-medium text-slate-700 dark:text-slate-300 truncate max-w-[200px]" title={log.procedure}>{log.procedure}</td>
                                                <td className="p-4 text-right">
                                                    {deleteConfirmId === log.id ? (
                                                        <div className="flex justify-end gap-2 items-center animate-in slide-in-from-right-2 fade-in duration-200">
                                                            <span className="text-xs font-bold text-red-600 mr-1">Sure?</span>
                                                            <button 
                                                                type="button"
                                                                onClick={(e) => confirmDelete(e, log.id)} 
                                                                className="px-3 py-1 bg-red-600 text-white hover:bg-red-700 rounded text-xs font-bold transition-colors focus:outline-none shadow-sm"
                                                            >
                                                                Delete
                                                            </button>
                                                            <button 
                                                                type="button"
                                                                onClick={cancelDelete} 
                                                                className="px-2 py-1 bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600 rounded text-xs font-bold transition-colors focus:outline-none"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex justify-end gap-1 items-center">
                                                            <button 
                                                                type="button"
                                                                onClick={(e) => handleEdit(e, log)} 
                                                                className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200 relative z-10" 
                                                                title="Edit Log"
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                            </button>
                                                            <button 
                                                                type="button"
                                                                onClick={(e) => initiateDelete(e, log.id)} 
                                                                className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-red-200 relative z-10" 
                                                                title="Delete Log"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                                    ))}
                                                </tbody>
                                            )}
                                        </React.Fragment>
                                        );
                                    })
                                )}
                            </table>
                        </div>
                    )}
                </div>
            </div>
        )}

        {activeTab === 'resident_log' && (
            <div className="flex-1 overflow-y-auto p-6">
                <div className="max-w-7xl mx-auto space-y-6">
                    <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg text-blue-700 dark:text-blue-400">
                                <Activity className="w-6 h-6" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-slate-900 dark:text-white brand-font">{editingSurgicalLogId ? 'Editing Surgical Case' : 'Surgical Case Logger'}</h1>
                                <p className="text-slate-500 dark:text-slate-400 text-xs font-medium">{editingSurgicalLogId ? 'Update details below' : 'Resident & Student Logbook'}</p>
                            </div>
                        </div>
                        
                        <div className="flex bg-slate-100 dark:bg-slate-900 p-1 rounded-lg">
                            <button
                                onClick={() => setLogView('log')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-bold transition-all ${
                                    logView === 'log' ? 'bg-white dark:bg-slate-700 text-blue-700 dark:text-blue-400 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                                }`}
                            >
                                <FileText className="w-4 h-4" />
                                Log Entry
                            </button>
                            <button
                                onClick={() => setLogView('summary')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-bold transition-all ${
                                    logView === 'summary' ? 'bg-white dark:bg-slate-700 text-blue-700 dark:text-blue-400 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                                }`}
                            >
                                <BarChart2 className="w-4 h-4" />
                                Summary
                            </button>
                        </div>
                    </div>

                    {logView === 'log' ? (
                        <>
                            <div className={`bg-white dark:bg-slate-800 p-6 rounded-xl shadow-sm border transition-all ${editingSurgicalLogId ? 'border-amber-400 dark:border-amber-600 ring-1 ring-amber-400 dark:ring-amber-600' : 'border-slate-200 dark:border-slate-700'}`}>
                                <form onSubmit={handleSurgicalSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Date</label>
                                            <input
                                                type="date"
                                                required
                                                className="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-900 outline-none text-sm text-slate-800 dark:text-white bg-white dark:bg-slate-900"
                                                value={surgicalForm.date}
                                                onChange={e => setSurgicalForm({...surgicalForm, date: e.target.value})}
                                            />
                                        </div>

                                        <div className="space-y-1">
                                            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Hospital</label>
                                            <select
                                                required
                                                className="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-900 outline-none bg-white dark:bg-slate-900 text-sm text-slate-800 dark:text-white"
                                                value={surgicalForm.hospital}
                                                onChange={e => setSurgicalForm({...surgicalForm, hospital: e.target.value as any})}
                                            >
                                                <option value="MVES">MVES</option>
                                                <option value="OPVES">OPVES</option>
                                            </select>
                                        </div>

                                        <div className="space-y-1">
                                            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Patient Name</label>
                                            <input
                                                type="text"
                                                required
                                                placeholder="e.g. Fluffy"
                                                className="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-900 outline-none text-sm text-slate-800 dark:text-white bg-white dark:bg-slate-900"
                                                value={surgicalForm.patientName}
                                                onChange={e => setSurgicalForm({...surgicalForm, patientName: e.target.value})}
                                            />
                                        </div>

                                        <div className="space-y-1">
                                            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Case Number</label>
                                            <input
                                                type="text"
                                                required
                                                placeholder="e.g. 12345"
                                                className="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-900 outline-none text-sm text-slate-800 dark:text-white bg-white dark:bg-slate-900"
                                                value={surgicalForm.caseNumber}
                                                onChange={e => setSurgicalForm({...surgicalForm, caseNumber: e.target.value})}
                                            />
                                        </div>

                                        <div className="space-y-1">
                                            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Attending</label>
                                            <select
                                                required
                                                className="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-900 outline-none bg-white dark:bg-slate-900 text-sm text-slate-800 dark:text-white"
                                                value={surgicalForm.attending}
                                                onChange={e => setSurgicalForm({...surgicalForm, attending: e.target.value})}
                                            >
                                                <option value="">Select Attending</option>
                                                {attendings.map(a => <option key={a} value={a}>{a}</option>)}
                                            </select>
                                        </div>

                                        <div className="space-y-1">
                                            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Resident 1</label>
                                            <select
                                                className="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-900 outline-none bg-white dark:bg-slate-900 text-sm text-slate-800 dark:text-white"
                                                value={surgicalForm.residentOne}
                                                onChange={e => setSurgicalForm({...surgicalForm, residentOne: e.target.value})}
                                            >
                                                <option value="">Select Resident (Opt)</option>
                                                {residents.map(r => <option key={r} value={r}>{r}</option>)}
                                            </select>
                                        </div>

                                        <div className="space-y-1">
                                            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Resident 2</label>
                                            <select
                                                className="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-900 outline-none bg-white dark:bg-slate-900 text-sm text-slate-800 dark:text-white"
                                                value={surgicalForm.residentTwo}
                                                onChange={e => setSurgicalForm({...surgicalForm, residentTwo: e.target.value})}
                                            >
                                                <option value="">Select Resident (Opt)</option>
                                                {residents.map(r => <option key={r} value={r}>{r}</option>)}
                                            </select>
                                        </div>

                                        <div className="space-y-1 lg:col-span-1">
                                            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                                Procedure <Search className="w-3 h-3" />
                                            </label>
                                            <input
                                                list="procedures-list"
                                                type="text"
                                                required
                                                placeholder="Type to search..."
                                                className="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-900 outline-none text-sm text-slate-800 dark:text-white bg-white dark:bg-slate-900"
                                                value={surgicalForm.procedure}
                                                onChange={e => setSurgicalForm({...surgicalForm, procedure: e.target.value})}
                                            />
                                            <datalist id="procedures-list">
                                                {PROCEDURES_LIST.map((p, idx) => (
                                                    <option key={idx} value={p} />
                                                ))}
                                            </datalist>
                                        </div>
                                    </div>

                                    <div className="space-y-4 pt-2">
                                        <div className="flex flex-col md:flex-row items-start gap-4">
                                            <div className="w-full md:w-auto flex-1">
                                                 <div 
                                                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${addToOnCall ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-800' : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700'}`} 
                                                    onClick={() => setAddToOnCall(!addToOnCall)}
                                                >
                                                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${addToOnCall ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-600'}`}>
                                                        {addToOnCall && <CheckCircle2 className="w-3.5 h-3.5" />}
                                                    </div>
                                                    <span className={`text-sm font-bold select-none ${addToOnCall ? 'text-indigo-900 dark:text-indigo-300' : 'text-slate-600 dark:text-slate-400'}`}>Also add to On-Call Tracker</span>
                                                </div>

                                                {addToOnCall && (
                                                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 animate-in slide-in-from-top-2 fade-in duration-200 p-4 bg-indigo-50/50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800">
                                                        <div className="space-y-1">
                                                            <label className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">Referring ER Doctor</label>
                                                            <input
                                                                type="text"
                                                                placeholder="e.g. Dr. Smith"
                                                                className="w-full p-2 border border-indigo-200 dark:border-indigo-800 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-sm bg-white dark:bg-slate-950 text-slate-900 dark:text-white"
                                                                value={onCallExtras.referringErDoctor}
                                                                onChange={e => setOnCallExtras({...onCallExtras, referringErDoctor: e.target.value})}
                                                            />
                                                        </div>
                                                        <div className="space-y-1">
                                                            <label className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">Anesthesia Support</label>
                                                            <input
                                                                type="text"
                                                                placeholder="e.g. Jess"
                                                                className="w-full p-2 border border-indigo-200 dark:border-indigo-800 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-sm bg-white dark:bg-slate-950 text-slate-900 dark:text-white"
                                                                value={onCallExtras.anesthesiaSupport}
                                                                onChange={e => setOnCallExtras({...onCallExtras, anesthesiaSupport: e.target.value})}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex gap-4 w-full md:w-auto items-start pt-1">
                                                 {editingSurgicalLogId && (
                                                    <button
                                                        type="button"
                                                        onClick={handleCancelSurgicalEdit}
                                                        className="px-6 py-3 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg font-bold shadow-sm transition-all hover:bg-slate-300 dark:hover:bg-slate-600"
                                                    >
                                                        Cancel
                                                    </button>
                                                )}
                                                 <button
                                                    type="submit"
                                                    className={`flex-1 px-8 py-3 ${editingSurgicalLogId ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-900 dark:bg-blue-600 hover:bg-blue-800 dark:hover:bg-blue-500'} text-white rounded-lg font-bold shadow-md transition-all flex items-center justify-center gap-2 whitespace-nowrap`}
                                                >
                                                    <Save className="w-5 h-5" />
                                                    {editingSurgicalLogId ? 'Update Case' : 'Log Case'}
                                                </button>
                                                <button 
                                                    type="button"
                                                    onClick={handleSurgicalExport}
                                                    className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-colors font-bold shadow-sm whitespace-nowrap"
                                                >
                                                    <Download className="w-4 h-4" />
                                                    Export CSV
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            <div className="flex border-b border-slate-300 dark:border-slate-700 gap-1 mt-8">
                                {['MVES', 'OPVES'].map(h => (
                                    <button
                                        key={h}
                                        onClick={() => setActiveHospitalTab(h as any)}
                                        className={`px-6 py-3 font-bold text-sm rounded-t-lg transition-colors ${
                                            activeHospitalTab === h 
                                            ? 'bg-white dark:bg-slate-800 border-x border-t border-slate-300 dark:border-slate-700 text-blue-900 dark:text-blue-300 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]' 
                                            : 'bg-slate-200 dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200'
                                        }`}
                                    >
                                        {h} Logs
                                    </button>
                                ))}
                            </div>

                            <div className="bg-white dark:bg-slate-800 rounded-b-xl rounded-tr-xl shadow-sm border border-slate-300 dark:border-slate-700 border-t-0 overflow-hidden">
                                <div className="p-4 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex flex-col sm:flex-row justify-between items-center gap-4">
                                    <div className="flex items-center gap-4">
                                         <h2 className="font-bold text-slate-700 dark:text-slate-300">{activeHospitalTab} Case History</h2>
                                         <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">
                                            {visibleSurgicalLogs.length} {surgicalSearch ? 'Found' : 'Total'}
                                         </span>
                                    </div>
                                    <div className="relative w-full sm:w-64">
                                        <input 
                                            type="text" 
                                            placeholder="Filter by Doctor, Patient..." 
                                            className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 focus:ring-2 focus:ring-blue-900 outline-none text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                            value={surgicalSearch}
                                            onChange={e => setSurgicalSearch(e.target.value)}
                                        />
                                        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                                    </div>
                                </div>
                                
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700">
                                            <tr>
                                                <th className="p-4 font-bold w-32">Date</th>
                                                <th className="p-4 font-bold w-40">Patient</th>
                                                <th className="p-4 font-bold w-24">Case #</th>
                                                <th className="p-4 font-bold">Staff & Residents</th>
                                                <th className="p-4 font-bold">Procedure</th>
                                                <th className="p-4 font-bold text-right w-20">Action</th>
                                            </tr>
                                        </thead>
                                        {groupedSurgicalLogs.length === 0 ? (
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                                <tr>
                                                    <td colSpan={6} className="p-12 text-center text-slate-400 italic">
                                                        {surgicalSearch ? 'No matching cases found.' : `No cases logged for ${activeHospitalTab} yet.`}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        ) : (
                                            groupedSurgicalLogs.map(({ month, logs }, index) => {
                                                const isExpanded = expandedSurgicalMonths[month] !== undefined ? expandedSurgicalMonths[month] : index === 0;
                                                return (
                                                <React.Fragment key={month}>
                                                    <tbody>
                                                        <tr 
                                                            className="bg-slate-50 dark:bg-slate-800 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" 
                                                            onClick={() => toggleSurgicalMonth(month)}
                                                        >
                                                            <td colSpan={6} className="p-3 font-bold text-slate-800 dark:text-slate-200 border-b border-slate-200 dark:border-slate-700">
                                                                <div className="flex items-center gap-2">
                                                                    {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                                                                    {month} <span className="text-xs font-normal text-slate-500 bg-white dark:bg-slate-900 px-2 py-0.5 rounded-full ml-2 border border-slate-200 dark:border-slate-700">{logs.length} cases</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                    {isExpanded && (
                                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                                            {logs.map((c) => (
                                                                <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                                                        <td className="p-4 text-slate-600 dark:text-slate-400 align-top font-mono">{c.date}</td>
                                                        <td className="p-4 font-bold text-slate-800 dark:text-slate-200 align-top">{c.patientName}</td>
                                                        <td className="p-4 text-slate-600 dark:text-slate-400 align-top font-mono">{c.caseNumber}</td>
                                                        <td className="p-4 text-slate-600 dark:text-slate-400 align-top">
                                                            <div className="flex flex-col gap-2">
                                                                <div className="text-xs">
                                                                    <span className="text-slate-400 font-bold uppercase mr-1">Attending:</span> 
                                                                    <span className="font-bold text-indigo-700 dark:text-indigo-400">{c.attending}</span>
                                                                </div>
                                                                
                                                                {(c.residentOne || c.residentTwo) && (
                                                                    <div className="space-y-1 mt-1">
                                                                        {c.residentOne && (
                                                                            <div className="flex items-center justify-between gap-2 bg-slate-50 dark:bg-slate-700 px-2 py-1 rounded border border-slate-100 dark:border-slate-600">
                                                                                <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{c.residentOne}</span>
                                                                                <button 
                                                                                    onClick={() => toggleSurgicalCert(c.id, 'residentOne')}
                                                                                    className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase transition-colors ${
                                                                                        c.residentOneCertLogged 
                                                                                            ? 'text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800' 
                                                                                            : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 border border-transparent'
                                                                                    }`}
                                                                                    title="Toggle CERT Logged"
                                                                                >
                                                                                    {c.residentOneCertLogged ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
                                                                                    CERT
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                        
                                                                        {c.residentTwo && (
                                                                            <div className="flex items-center justify-between gap-2 bg-slate-50 dark:bg-slate-700 px-2 py-1 rounded border border-slate-100 dark:border-slate-600">
                                                                                <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{c.residentTwo}</span>
                                                                                <button 
                                                                                    onClick={() => toggleSurgicalCert(c.id, 'residentTwo')}
                                                                                    className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase transition-colors ${
                                                                                        c.residentTwoCertLogged 
                                                                                            ? 'text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800' 
                                                                                            : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 border border-transparent'
                                                                                    }`}
                                                                                    title="Toggle CERT Logged"
                                                                                >
                                                                                    {c.residentTwoCertLogged ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
                                                                                    CERT
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="p-4 text-slate-600 dark:text-slate-400 align-top">
                                                            <div className="max-w-xs text-xs font-medium bg-slate-100 dark:bg-slate-700 p-2 rounded border border-slate-200 dark:border-slate-600" title={c.procedure}>{c.procedure}</div>
                                                        </td>
                                                        <td className="p-4 text-right align-top">
                                                            <div className="flex justify-end gap-1">
                                                                <button 
                                                                    onClick={() => handleEditSurgicalLog(c)}
                                                                    className="text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 p-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                                                    title="Edit"
                                                                >
                                                                    <Edit2 className="w-4 h-4" />
                                                                </button>
                                                                <button 
                                                                    onClick={() => initiateSurgicalDelete(c)}
                                                                    className="text-slate-300 hover:text-red-600 dark:hover:text-red-400 p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                                    title="Delete"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                            ))}
                                                        </tbody>
                                                    )}
                                                </React.Fragment>
                                                );
                                            })
                                        )}
                                    </table>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="space-y-8">
                            <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                                <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-2 brand-font">Program Dashboard</h2>
                                <p className="text-slate-500 dark:text-slate-400 text-sm">Overview of surgical category distribution.</p>
                            </div>

                            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-blue-200 dark:border-blue-900/50 overflow-hidden">
                                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-900/50 flex justify-between items-center">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-blue-600 rounded-lg shadow-sm">
                                            <Activity className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-slate-900 dark:text-white text-lg">Total Program Volume</h3>
                                            <p className="text-xs text-blue-600 dark:text-blue-400 font-bold uppercase tracking-wide">All Hospitals (MVES & OPVES)</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className="block text-3xl font-black text-slate-900 dark:text-white">{surgicalLogs.length}</span>
                                        <span className="text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wide">Total Procedures</span>
                                    </div>
                                </div>
                                
                                <div className="p-0">
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wider border-b border-slate-200 dark:border-slate-700 font-bold">
                                            <tr>
                                                <th className="px-6 py-3">Surgical Category</th>
                                                <th className="px-6 py-3 text-right">Count</th>
                                                <th className="px-6 py-3 text-right w-32">% of Total</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                            {Object.keys(surgicalGlobalStats).length === 0 ? (
                                                <tr><td colSpan={3} className="p-8 text-center text-slate-400 italic">No cases logged yet.</td></tr>
                                            ) : (
                                                Object.entries(surgicalGlobalStats)
                                                    .sort(([,a], [,b]) => Number(b) - Number(a))
                                                    .map(([category, count]) => (
                                                        <tr key={category} className="hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors">
                                                            <td className="px-6 py-3 text-slate-700 dark:text-slate-300 font-bold break-words">{category}</td>
                                                            <td className="px-6 py-3 text-right font-bold text-slate-900 dark:text-white">{count}</td>
                                                            <td className="px-6 py-3 text-right text-slate-500 dark:text-slate-400 font-mono">
                                                                {Math.round((Number(count) / (surgicalLogs.length || 1)) * 100)}%
                                                            </td>
                                                        </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <h3 className="text-xl font-bold text-slate-800 dark:text-white mt-8 px-1 brand-font">Individual Statistics</h3>
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                {[...attendings, ...residents].sort().map(doctor => {
                                    const { stats, total } = getDoctorStats(doctor);
                                    if (total === 0) return null; 
                                    const isExpanded = expandedDoctors[doctor];
                                    
                                    return (
                                        <div key={doctor} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col transition-all">
                                            <div 
                                                className="p-4 bg-slate-50 dark:bg-slate-900 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex justify-between items-center group"
                                                onClick={() => toggleDoctorStats(doctor)}
                                            >
                                                <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2 group-hover:text-blue-700 dark:group-hover:text-blue-400 transition-colors">
                                                    Dr. {doctor}
                                                    {residents.includes(doctor) && <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold">Resident</span>}
                                                    {attendings.includes(doctor) && <span className="bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold">Attending</span>}
                                                </h3>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-sm font-bold text-slate-500 dark:text-slate-400">{total} Cases</span>
                                                    {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                                                </div>
                                            </div>
                                            
                                            {isExpanded && (
                                                <div className="p-0 border-t border-slate-200 dark:border-slate-700 animate-in slide-in-from-top-2 duration-200">
                                                    <table className="w-full text-left text-sm">
                                                        <thead className="bg-white dark:bg-slate-800 text-slate-400 text-xs uppercase tracking-wider border-b border-slate-100 dark:border-slate-700 font-bold">
                                                            <tr>
                                                                <th className="px-4 py-2">Category</th>
                                                                <th className="px-4 py-2 text-right">Count</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-50 dark:divide-slate-700">
                                                            {Object.entries(stats)
                                                                .sort(([,a], [,b]) => Number(b) - Number(a))
                                                                .map(([category, count]) => (
                                                                    <tr key={category} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                                                                        <td className="px-4 py-2 text-slate-600 dark:text-slate-300 text-xs break-words">{category}</td>
                                                                        <td className="px-4 py-2 text-right font-bold text-slate-800 dark:text-white">{count}</td>
                                                                    </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )}

        {activeTab === 'master' && (
          <div className="w-full h-full flex flex-col animate-in fade-in duration-300">
              <div className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-2 flex justify-center items-center gap-4">
                  <a 
                    href={masterSheetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 shadow-sm ring-1 ring-slate-200 dark:ring-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 transition-all hover:shadow-md"
                  >
                      <FileSpreadsheet className="w-4 h-4" /> 2025 On-Call Sheet
                  </a>
              </div>
              <div className="flex-1 relative bg-white dark:bg-slate-900">
                <iframe 
                    src="https://docs.google.com/spreadsheets/d/1hbA6cOIa9dvsXhbR8W6J12-C3HNTSPh59oREIetPEQ8/edit?gid=0#gid=0"
                    className="w-full h-full border-none"
                    title="Google Sheets Log"
                    allow="clipboard-write"
                ></iframe>
              </div>
          </div>
        )}
      </div>

      {isModalOpen && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[90vh] border border-slate-200 dark:border-slate-700">
                  <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex justify-between items-center">
                      <h3 className="font-bold text-slate-800 dark:text-white brand-font">
                          {editingLogId ? 'Edit Case Log' : 'Add New Case'}
                      </h3>
                      <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-white"><X className="w-5 h-5" /></button>
                  </div>
                  
                  <div className="p-6 space-y-4 overflow-y-auto">
                      <div className="grid grid-cols-3 gap-4">
                          <div>
                              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Date</label>
                              <input type="date" className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                  value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
                          </div>
                          <div>
                              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Location</label>
                              <select className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                  value={form.location} onChange={e => setForm({...form, location: e.target.value as any})}>
                                  <option value="MVES">MVES</option>
                                  <option value="OPVES">OPVES</option>
                              </select>
                          </div>
                          <div>
                              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Case #</label>
                              <input className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                  value={form.caseNumber} onChange={e => setForm({...form, caseNumber: e.target.value})} placeholder="e.g. 10045" />
                          </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Patient First Name</label>
                              <input className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                  value={form.patientFirstName} onChange={e => setForm({...form, patientFirstName: e.target.value})} placeholder="First" />
                          </div>
                          <div>
                              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Patient Last Name</label>
                              <input className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                  value={form.patientLastName} onChange={e => setForm({...form, patientLastName: e.target.value})} placeholder="Last" />
                          </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <label className="block text-xs font-bold text-indigo-700 dark:text-indigo-400 uppercase mb-1">Attending</label>
                              <select 
                                  className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-900 uppercase text-slate-900 dark:text-white"
                                  value={form.attending} onChange={(e) => setForm({...form, attending: e.target.value})}
                              >
                                  <option value="">-- Select --</option>
                                  {attendings.map(d => <option key={d} value={d}>{d}</option>)}
                              </select>
                          </div>
                          <div>
                              <label className="block text-xs font-bold text-blue-700 dark:text-blue-400 uppercase mb-1">Resident</label>
                              <select 
                                  className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-900 uppercase text-slate-900 dark:text-white"
                                  value={form.resident} onChange={(e) => setForm({...form, resident: e.target.value})}
                              >
                                  <option value="">-- Select --</option>
                                  {residents.map(d => <option key={d} value={d}>{d}</option>)}
                              </select>
                          </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                           <div>
                              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Anesthesia Support</label>
                              <input className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                  value={form.anesthesiaSupport} onChange={e => setForm({...form, anesthesiaSupport: e.target.value})} placeholder="e.g. Jess" />
                          </div>
                          <div>
                              <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Referring ER Doctor</label>
                              <input className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                                  value={form.referringErDoctor} onChange={e => setForm({...form, referringErDoctor: e.target.value})} placeholder="Dr. Name" />
                          </div>
                      </div>

                      <div>
                          <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1">Procedure</label>
                          <input className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-900 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900" 
                              value={form.procedure} onChange={e => setForm({...form, procedure: e.target.value})} placeholder="Full procedure name" />
                      </div>

                      <div className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer" onClick={() => setForm({...form, payrollCompleted: !form.payrollCompleted})}>
                          <button className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${form.payrollCompleted ? 'bg-green-500 border-green-600 text-white' : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600'}`}>
                              {form.payrollCompleted && <CheckCircle2 className="w-3.5 h-3.5" />}
                          </button>
                          <span className="text-sm font-bold text-slate-700 dark:text-slate-300">Mark as Paid / Accounted For</span>
                      </div>
                  </div>

                  <div className="p-4 bg-slate-50 dark:bg-slate-900 flex justify-end gap-2 border-t border-slate-200 dark:border-slate-700">
                      <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-slate-600 dark:text-slate-400 font-medium hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg">Cancel</button>
                      <button onClick={handleSave} className="px-4 py-2 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 flex items-center gap-2">
                          <Save className="w-4 h-4" /> Save Case
                      </button>
                  </div>
              </div>
          </div>
      )}

      {surgicalCaseToDelete && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-sm w-full p-6 text-center animate-in zoom-in-95 duration-200">
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-red-50 dark:border-red-900/50">
                      <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 brand-font">Delete Log Entry?</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-sm mb-6 leading-relaxed">
                      Are you sure you want to permanently delete the surgical log for <span className="font-bold text-slate-800 dark:text-white">{surgicalCaseToDelete.patientName}</span>?
                  </p>
                  <div className="flex gap-3 justify-center">
                      <button 
                          onClick={() => setSurgicalCaseToDelete(null)}
                          className="px-4 py-2.5 text-slate-600 dark:text-slate-400 font-bold hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors w-full"
                      >
                          Cancel
                      </button>
                      <button 
                          onClick={confirmSurgicalDelete}
                          className="px-4 py-2.5 bg-red-600 dark:bg-red-500 text-white font-bold rounded-xl hover:bg-red-700 dark:hover:bg-red-600 transition-colors shadow-lg shadow-red-900/20 w-full"
                      >
                          Yes, Delete
                      </button>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};
